import { getContrastRatio } from '@mui/material/styles';
import he from 'he';
import { parseColorToRgba, rgbaToRgbString, compositeColors } from './colorUtils.js';
import numeral from 'numeral';

const escapeUriPath = (value, spaceReplacementChar) => {
    let valueString = `${value}`;
    valueString = valueString
        .trim()
        .replace(/[^\w\s]/gi, '')
        .replace(/ +/g, spaceReplacementChar ? spaceReplacementChar : '-');
    return valueString;
};
const changeAmazonImageUrlResolution = (imageUrl, resolution) => {
    const reg = /_SL\d+/;
    return imageUrl.includes('media-amazon.com') && imageUrl.includes('_SL')
        ? imageUrl.replace(reg, `_SL${resolution}`)
        : imageUrl;
};
const isParentProduct = (stacklineSku) => {
    const regex = new RegExp('^(?:[0-9]_{1}:?){1}');
    return regex.test(stacklineSku);
};
const removeTrailingSlash = (str) => {
    return str.endsWith('/') ? str.slice(0, -1) : str;
};
const getRetailerFromId = (retailerId) => {
    const retailers = {
        1: 'Amazon',
        2: 'Walmart',
        3: 'Best Buy',
        4: 'Target',
        6: 'Home Depot',
        9: 'Costco',
        18: 'Chewy',
        48: 'Kroger',
        50: 'Sephora',
        92: 'Safeway',
        94: 'Albertsons',
        127: 'Fred Meyer',
        128: 'Ralphs',
        129: 'Kroger',
        116: 'PetSmart',
    };
    return retailers[retailerId] || `${retailerId}`;
};
const isWhite = (color) => {
    const patterns = [
        /^white$/,
        /^#fff$/,
        /^#ffffff$/,
        /^rgb\(255,\s*255,\s*255\)$/i,
        /^rgba\(255,\s*255,\s*255,?\s*(1|1\.0*)\)$/i, // RGBA with opacity 1 or omitted
    ];
    return patterns.some((pattern) => pattern.test(color));
};
const roundFloat = (value, decimals = 4) => {
    return value ? parseFloat(Number(value).toFixed(decimals)) : 0;
};
const formatMoney = (num, currencySymbol = '$', includeDecimal = true) => {
    return numeral(roundFloat(num)).format(`${currencySymbol}0,0${includeDecimal ? `.00` : ``}`);
};
/**
 * Decode a string of HTML text
 *
 * This function takes a string of HTML and decodes any named
 * and numerical character references
 **/
const decodeHTMLCharacters = (str) => he.decode(str);
/**
 * Returns a color that meets the contrast requirements against the given
 * background. If a `foregroundColor` is supplied, its contrast is verified
 * (taking its alpha channel into account).  When it fails the threshold,
 * the function falls back to `theme.palette.getContrastText(backgroundColor)`.
 *
 * @param theme            MUI theme containing contrast settings
 * @param backgroundColor  Background color to test against
 * @param foregroundColor  Optional preferred foreground color
 */
const getContrastColor = (theme, backgroundColor, foregroundColor) => {
    const candidate = foregroundColor || theme.palette.primary.main;
    // Parse colors – if parsing fails, defer to MUI's helper.
    const bgRgba = parseColorToRgba(backgroundColor);
    const fgRgba = parseColorToRgba(candidate);
    if (!bgRgba || !fgRgba) {
        return theme.palette.getContrastText(backgroundColor);
    }
    // Blend when the candidate is not fully opaque.
    const effectiveFg = fgRgba.a < 1 ? rgbaToRgbString(compositeColors(bgRgba, fgRgba)) : candidate;
    const contrastRatio = getContrastRatio(backgroundColor, effectiveFg);
    return contrastRatio >= theme.palette.contrastThreshold ? candidate : theme.palette.getContrastText(backgroundColor);
};
const getNumericValue = (value) => {
    try {
        const numericValue = typeof value === 'number' ? value : parseFloat(value);
        if (Number.isFinite(numericValue)) {
            return numericValue;
        }
    }
    catch (e) {
        console.warn('Error getting numeric value', e);
    }
};
const DEFAULT_MUI_THEME_FONT_SIZE = 14;
const DEFAULT_BROWSER_HTML_FONT_SIZE = 16;
const pxToRem = ({ pxFontSize, baseFontSize = DEFAULT_MUI_THEME_FONT_SIZE, htmlFontSize = DEFAULT_BROWSER_HTML_FONT_SIZE, }) => {
    if (typeof pxFontSize === 'string' && pxFontSize.includes('rem')) {
        return pxFontSize;
    }
    const numericPxFontSize = getNumericValue(pxFontSize);
    if (numericPxFontSize == null) {
        console.error('Invalid pxFontSize', pxFontSize);
        return '0rem';
    }
    const coef = baseFontSize / DEFAULT_MUI_THEME_FONT_SIZE;
    return `${(numericPxFontSize / htmlFontSize) * coef}rem`;
};
const remToPx = ({ remFontSize, baseFontSize = DEFAULT_MUI_THEME_FONT_SIZE, htmlFontSize = DEFAULT_BROWSER_HTML_FONT_SIZE, }) => {
    const coef = baseFontSize / DEFAULT_MUI_THEME_FONT_SIZE;
    return remFontSize * htmlFontSize * coef;
};
const getRootFontSize = () => {
    const rootElement = document.documentElement;
    const rootFontSize = window.getComputedStyle(rootElement).fontSize;
    return getNumericValue(rootFontSize);
};
const parseThemeUrl = (url) => {
    if (!url) {
        return url;
    }
    if (url.startsWith('url(') && url.endsWith(')')) {
        // remove the url() wrapper
        return url.slice(4, -1);
    }
    return url;
};

export { changeAmazonImageUrlResolution, decodeHTMLCharacters, escapeUriPath, formatMoney, getContrastColor, getNumericValue, getRetailerFromId, getRootFontSize, isParentProduct, isWhite, parseThemeUrl, pxToRem, remToPx, removeTrailingSlash, roundFloat };
//# sourceMappingURL=stringUtils.js.map
